import React, { useEffect, useState } from "react";
import { Clock, User, Check, X, Loader, AlertCircle, MessageSquare, ShieldCheck } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import server from "../../../utils/serverConfig";
import toast from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";

const brokerColors = {
  fyers: "bg-blue-800 text-white",
  dhan_hq: "bg-emerald-800 text-white",
  nubra: "bg-purple-800 text-white",
  zerodha: "bg-red-800 text-white",
  mosl: "bg-amber-800 text-white",
};

export default function ApprovalsPage({ onApprovalsChange }) {
  const [activeTab, setActiveTab] = useState("pending");
  const [pendingList, setPendingList] = useState([]);
  const [approvedList, setApprovedList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Rejection Dialog states
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);

  // Approval state
  const [approvingId, setApprovingId] = useState(null);

  const fetchLists = async () => {
    setIsLoading(true);
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        apiClient.get(`${server.godseye.baseUrl}crm/onboarding/approvals`),
        apiClient.get(`${server.godseye.baseUrl}crm/onboarding/approved`),
      ]);
      const pendingData = pendingRes.data || [];
      setPendingList(pendingData);
      setApprovedList(approvedRes.data || []);
      if (onApprovalsChange) {
        onApprovalsChange(pendingData.length);
      }
    } catch (error) {
      console.error("Failed to fetch approvals data:", error);
      toast.error("Failed to load approvals lists");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLists();
  }, []);

  const handleApprove = async (id, clientName) => {
    setApprovingId(id);
    try {
      const response = await apiClient.post(`${server.godseye.baseUrl}crm/onboarding/${id}/approve`);
      if (response.status === 200) {
        toast.success(`Account for ${clientName} successfully approved and active!`);
        fetchLists();
      }
    } catch (error) {
      console.error("Failed to approve account:", error);
      const errMsg = error.response?.data?.detail || "Approval failed";
      toast.error(`Approval failed: ${errMsg}`);
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectTarget) return;
    setIsRejecting(true);
    try {
      const response = await apiClient.post(
        `${server.godseye.baseUrl}crm/onboarding/${rejectTarget.id}/reject`,
        { note: rejectNote.trim() }
      );
      if (response.status === 200) {
        toast.success(`Account for ${rejectTarget.name} rejected and reset.`);
        setRejectTarget(null);
        setRejectNote("");
        fetchLists();
      }
    } catch (error) {
      console.error("Failed to reject account:", error);
      toast.error("Rejection failed");
    } finally {
      setIsRejecting(false);
    }
  };

  const renderProductTags = (item) => {
    const products = [];
    if (item.product_is_optimus) products.push("Optimus");
    if (item.product_is_pledgeplus) products.push("PledgePlus");
    if (item.product_is_polaris) products.push("Polaris");
    if (item.product_is_polaris_lite) products.push("Polaris Lite");
    return products.map((p) => (
      <span key={p} className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[9px] font-semibold">
        {p}
      </span>
    ));
  };

  const renderCredField = (label, value) => {
    if (!value) return null;
    return (
      <div className="flex flex-col">
        <span className="text-zinc-500 text-[10px] uppercase font-semibold">{label}</span>
        <span className="text-zinc-300 text-xs font-mono truncate max-w-[150px]" title={value}>
          ••••••••
        </span>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-[#0a0c14] font-inter">
      {/* Tab bar */}
      <div className="flex items-center justify-between border-b border-zinc-800/60 bg-[#0d0f18] px-6 py-2">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab("pending")}
            className={`py-3 text-xs font-semibold relative transition-colors ${
              activeTab === "pending" ? "text-blue-500" : "text-zinc-400 hover:text-white"
            }`}
          >
            Pending Review
            <span className="ml-2 bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded text-[10px]">
              {pendingList.length}
            </span>
            {activeTab === "pending" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded" />
            )}
          </button>
          <button
            onClick={() => setActiveTab("approved")}
            className={`py-3 text-xs font-semibold relative transition-colors ${
              activeTab === "approved" ? "text-blue-500" : "text-zinc-400 hover:text-white"
            }`}
          >
            Approved History
            <span className="ml-2 bg-zinc-800 text-zinc-400 border border-zinc-700/40 px-2 py-0.5 rounded text-[10px]">
              {approvedList.length}
            </span>
            {activeTab === "approved" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded" />
            )}
          </button>
        </div>
        <button
          onClick={fetchLists}
          className="text-zinc-500 hover:text-white p-2 rounded hover:bg-zinc-800/60 text-xs font-semibold"
        >
          Refresh
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: "thin" }}>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader className="w-8 h-8 text-blue-500 animate-spin mb-3" />
            <p className="text-zinc-400 text-sm">Loading approvals...</p>
          </div>
        ) : activeTab === "pending" ? (
          pendingList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-full bg-zinc-800/60 flex items-center justify-center mb-4">
                <Clock className="w-7 h-7 text-zinc-600" />
              </div>
              <p className="text-zinc-400 text-sm font-medium">No pending approvals</p>
              <p className="text-zinc-600 text-xs mt-1">All submitted accounts have been processed</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pendingList.map((item) => (
                <div
                  key={item.id}
                  className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-5 hover:border-zinc-700/60 transition-all duration-200 flex flex-col justify-between"
                >
                  <div className="space-y-4">
                    {/* User and Broker Header */}
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-white text-sm font-semibold">{item.name || "Unnamed"}</h4>
                          {renderProductTags(item)}
                        </div>
                        <p className="text-zinc-500 text-xs">{item.email} • {item.phone_number}</p>
                      </div>
                      <span className={`px-2.5 py-0.5 rounded text-[10px] font-bold uppercase ${brokerColors[item.broker.toLowerCase().replace(/[^a-z0-9_]/g, "").replace("dhanhq", "dhan_hq")] || "bg-zinc-800 text-zinc-400"}`}>
                        {item.broker}
                      </span>
                    </div>

                    {/* Client ID / Broker Details info */}
                    <div className="grid grid-cols-2 gap-3 py-3 border-t border-b border-zinc-800/60">
                      <div>
                        <span className="text-zinc-500 text-[10px] uppercase font-semibold">Client ID</span>
                        <span className="text-white text-xs font-mono block mt-0.5">{item.client_id || "-"}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-[10px] uppercase font-semibold">Submitted At</span>
                        <span className="text-zinc-400 text-xs block mt-0.5">
                          {item.updated_at ? new Date(item.updated_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          }) : "-"}
                        </span>
                      </div>
                    </div>

                    {/* Legality Status info */}
                    <div className="grid grid-cols-2 gap-3 pb-3 border-b border-zinc-800/60 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-500 text-[10px] uppercase font-semibold">Doc Sent:</span>
                        {item.is_leegality_doc_sent ? (
                          <span className="inline-flex items-center gap-0.5 text-emerald-400 font-medium">
                            <Check className="w-3.5 h-3.5 text-emerald-400" /> Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 text-red-400 font-medium">
                            <X className="w-3.5 h-3.5 text-red-400" /> No
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-500 text-[10px] uppercase font-semibold">Legality Complete:</span>
                        {item.is_legalities_complete ? (
                          <span className="inline-flex items-center gap-0.5 text-emerald-400 font-medium">
                            <Check className="w-3.5 h-3.5 text-emerald-400" /> Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 text-red-400 font-medium">
                            <X className="w-3.5 h-3.5 text-red-400" /> No
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Credentials Preview (Masked) */}
                    <div className="grid grid-cols-3 gap-2 bg-zinc-950/20 p-2.5 rounded-lg border border-zinc-800/30">
                      {renderCredField("TOTP Key", item[`${item.broker}_totp_key`])}
                      {renderCredField("PIN / Pass", item[`${item.broker}_pin`] || item[`${item.broker}_password`])}
                      {renderCredField("API Key", item[`${item.broker}_api_key`])}
                      {renderCredField("API Secret", item[`${item.broker}_api_secret`] || item[`${item.broker}_api_secret_key`])}
                    </div>

                    {/* Show existing admin notes if any */}
                    {item.notes && item.notes.trim() && (
                      <div className="flex gap-2 text-zinc-500 text-xs bg-zinc-800/20 p-2.5 rounded border border-zinc-800/40">
                        <MessageSquare className="w-4 h-4 text-zinc-600 flex-shrink-0 mt-0.5" />
                        <span className="italic break-words whitespace-pre-wrap">{item.notes.trim()}</span>
                      </div>
                    )}
                  </div>

                  {/* Approve / Reject Actions */}
                  <div className="flex items-center gap-3 mt-5">
                    <button
                      onClick={() => setRejectTarget({ id: item.id, name: item.name })}
                      disabled={approvingId === item.id}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold bg-red-950/40 text-red-400 border border-red-900/30 hover:bg-red-900/40 transition-all disabled:opacity-40"
                    >
                      <X className="w-3.5 h-3.5" />
                      Reject & Reset
                    </button>
                    <button
                      onClick={() => handleApprove(item.id, item.name)}
                      disabled={approvingId !== null}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold bg-[#07F2C7] text-black hover:bg-[#06d5af] transition-all disabled:opacity-40"
                    >
                      {approvingId === item.id ? (
                        <Loader className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Check className="w-3.5 h-3.5" />
                      )}
                      {approvingId === item.id ? "Approving..." : "Approve Account"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          approvedList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-full bg-zinc-800/60 flex items-center justify-center mb-4">
                <ShieldCheck className="w-7 h-7 text-zinc-600" />
              </div>
              <p className="text-zinc-400 text-sm font-medium">No approved accounts found</p>
              <p className="text-zinc-600 text-xs mt-1">Approved accounts will be listed here as history</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-zinc-800/60 rounded-xl bg-[#0d0f18]/30">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-zinc-800/80 bg-[#0d0f18] text-zinc-400 font-semibold uppercase tracking-wider">
                    <th className="py-3.5 px-4">Client Name</th>
                    <th className="py-3.5 px-4">Broker</th>
                    <th className="py-3.5 px-4">Client ID</th>
                    <th className="py-3.5 px-4">Products</th>
                    <th className="py-3.5 px-4">Doc Sent</th>
                    <th className="py-3.5 px-4">Legality</th>
                    <th className="py-3.5 px-4">Approved At</th>
                    <th className="py-3.5 px-4 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/60 text-zinc-300">
                  {approvedList.map((item) => (
                    <tr key={item.id} className="hover:bg-zinc-800/20 transition-colors">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-semibold text-white">{item.name}</p>
                          <p className="text-[10px] text-zinc-500 mt-0.5">{item.email || "-"}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${brokerColors[item.broker.toLowerCase().replace(/[^a-z0-9_]/g, "").replace("dhanhq", "dhan_hq")] || "bg-zinc-800 text-zinc-400"}`}>
                          {item.broker}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-zinc-400">{item.client_id || "-"}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1.5 flex-wrap">
                          {renderProductTags(item)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {item.is_leegality_doc_sent ? (
                          <span className="inline-flex items-center gap-0.5 text-emerald-400 font-medium">
                            <Check className="w-3.5 h-3.5 text-emerald-400" /> Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 text-red-400 font-medium">
                            <X className="w-3.5 h-3.5 text-red-400" /> No
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {item.is_legalities_complete ? (
                          <span className="inline-flex items-center gap-0.5 text-emerald-400 font-medium">
                            <Check className="w-3.5 h-3.5 text-emerald-400" /> Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 text-red-400 font-medium">
                            <X className="w-3.5 h-3.5 text-red-400" /> No
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-zinc-400">
                        {item.admin_approved_date ? new Date(item.admin_approved_date).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        }) : "-"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-semibold">
                          <Check className="w-3 h-3" />
                          Approved
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Reject Reason Dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <DialogContent className="sm:max-w-[450px] border-zinc-800 bg-[#0f1116] text-white font-inter">
          <DialogHeader className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-red-950/40 border border-red-900/30 flex items-center justify-center mb-3">
              <X className="w-5 h-5 text-red-400" />
            </div>
            <DialogTitle className="text-white text-base font-semibold">Reject & Reset Application</DialogTitle>
          </DialogHeader>

          <div className="py-3 space-y-4">
            <p className="text-zinc-400 text-sm text-center leading-relaxed">
              Are you sure you want to reject the broker details for <strong className="text-white">{rejectTarget?.name}</strong>?
              This will reset the application back to Step 3, enabling them to re-enter their credentials.
            </p>
            <div className="space-y-1.5">
              <label className="text-zinc-500 text-xs font-semibold uppercase">Rejection Reason / Notes</label>
              <Input
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="e.g. Invalid TOTP secret key format, please re-check"
                className="bg-zinc-950 border-zinc-850 text-white placeholder-zinc-600 text-xs py-2 px-3 focus-visible:ring-1 focus-visible:ring-red-500"
              />
            </div>
          </div>

          <DialogFooter className="mt-4 flex gap-2 sm:gap-0 justify-center sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={isRejecting}
              onClick={() => {
                setRejectTarget(null);
                setRejectNote("");
              }}
              className="bg-zinc-850 border-zinc-750 hover:bg-zinc-800 hover:text-white text-zinc-300 text-xs font-medium px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isRejecting}
              onClick={handleRejectSubmit}
              className="bg-red-600 hover:bg-red-500 text-white text-xs font-semibold px-4 py-2"
            >
              {isRejecting ? "Rejecting..." : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
